'use client';

import { type ReactElement } from 'react';
import { useToggle, useWindowSize } from 'react-use';
import { ArrowLeftCircle } from '@ironhack/design-system2/icons/arrow-left-circle';
import { ArrowRightCircle } from '@ironhack/design-system2/icons/arrow-right-circle';
import { Icon } from '@ironhack/design-system2/components/icon';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
} from '@ironhack/design-system2/components/modal';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';

import { type Story } from '@/app/components/client/stories';
import { StoryPlayerMobile } from '@/app/components/client/story-player-mobile';
import { StoryPlayerDesktop } from '@/app/components/client/story-player-desktop';

type Props = {
  onExit: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  story: Story;
};

export const StoryPlayer = (props: Props): ReactElement => {
  const { onExit, onNext, onPrevious } = props;
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [showModal, toggleShowModal] = useToggle(true);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const StoryPlayerComponent = isMobile
    ? StoryPlayerMobile
    : StoryPlayerDesktop;

  const handleOnClose = () => {
    toggleShowModal();
    onExit();
  };

  return (
    <Modal isOpen={showModal} onClose={handleOnClose}>
      <ModalContent
        bg="black"
        h={windowHeight}
        m={0}
        maxH="none"
        maxW="none"
        rounded="none"
        w={windowWidth}
      >
        {!isMobile && <ModalCloseButton color="white" />}
        <ModalBody
          alignItems="center"
          display="flex"
          gap={3}
          justifyContent="center"
          p={0}
        >
          {!isMobile && (
            <Icon
              as={ArrowLeftCircle}
              boxSize="32px"
              color={onPrevious ? 'white' : 'stroke.medium'}
              cursor={onPrevious ? 'pointer' : 'not-allowed'}
              {...(onPrevious && { onClick: onPrevious })}
            />
          )}
          <StoryPlayerComponent {...props} />
          {!isMobile && (
            <Icon
              as={ArrowRightCircle}
              boxSize="32px"
              color={onNext ? 'white' : 'stroke.medium'}
              cursor={onNext ? 'pointer' : 'not-allowed'}
              {...(onNext && { onClick: onNext })}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
